import { RefreshCw, Download } from "lucide-react";
import { Button } from "@/components/reusable";
import type { AnalyticsSummary } from "@/types/analytics";

interface AnalyticsHeaderProps {
  summary: AnalyticsSummary | null;
  loading?: boolean;
  onRefresh?: () => void;
  onExport?: () => void;
}

function fmtDate(iso: string): string {
  // "2023-12-14" → "14 Dec 2023"
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

/**
 * Policy-scoped analytics header. Sits between the PageBanner and the filter
 * bar. Mirrors the sample's compact layout (breadcrumb / title / meta / actions).
 */
export function AnalyticsHeader({
  summary,
  loading = false,
  onRefresh,
  onExport,
}: AnalyticsHeaderProps) {
  return (
    <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
      <div className="flex flex-col gap-1 min-w-0">
        <div className="text-[11px] uppercase tracking-widest text-[var(--color-text-tertiary)] font-medium">
          Bordereaux analytics / Marine Hull
        </div>
        <div className="text-[20px] font-semibold tracking-tight text-[var(--color-text-primary)] leading-tight">
          {loading || !summary ? (
            <span className="inline-block h-5 w-64 bg-[var(--color-bg-tertiary)] rounded animate-pulse" />
          ) : (
            <>Vessel Schedule — {summary.policyRef}</>
          )}
        </div>
        <div
          className="text-[12px] text-[var(--color-text-secondary)]"
          style={{ fontFamily: "var(--font-mono)" }}
        >
          {summary ? (
            <>
              Inception: {fmtDate(summary.inceptionDate)} &nbsp;·&nbsp; Expiry:{" "}
              {fmtDate(summary.expiryDate)}
            </>
          ) : null}
        </div>
      </div>
      <div className="flex gap-2 items-center flex-wrap">
        <Button variant="secondary" size="sm" onClick={onRefresh}>
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </Button>
        <Button variant="primary" size="sm" onClick={onExport}>
          <Download className="w-3.5 h-3.5" />
          Export
        </Button>
      </div>
    </div>
  );
}
